<template>
  <MetaInfo
    :title="meta.title"
    :description="meta.description"
  ></MetaInfo>

  <div class="support-page">
    <navigation></navigation>

    <!-- Left Sidebar -->
    <support-sidebar></support-sidebar>

    <div class="support-content">
      <div class="container-fluid">
        <div class="row">
          <div class="col-sm-12">
          <!-- Error Message -->
          <div v-if="error" class="support-error">
            <h3>Error Loading Collection</h3>
            <p><strong>{{ error.message }}</strong></p>
            <p v-if="error.isAuthError">
              Please check that your HelpScout API key is correctly configured in your <code>.env.local</code> file.
            </p>
          </div>

          <!-- Loading State -->
          <div v-if="loading && !error">
            <skeleton-loader type="collection" :item-count="3"></skeleton-loader>
          </div>

          <!-- Collection Content -->
          <div v-if="!loading && !error && collection" class="support-collection">
            <div class="support-collection__header">
              <h2 class="h3">{{ collection.name }}</h2>
              <p v-if="collection.description" class="support-collection__description">
                {{ collection.description }}
              </p>
            </div>

            <!-- Categories -->
            <div v-if="categories.length > 0" class="support-collection__categories">
              <div 
                v-for="category in categories" 
                :key="category.id" 
                class="support-category"
              >
                <div class="support-category__header">
                  <h5>{{ category.name }}</h5>
                  <p v-if="category.description" class="support-category__description">
                    {{ category.description }}
                  </p>
                </div>
                
                <!-- Articles in this category -->
                <div v-if="category.articles && category.articles.length > 0" class="support-category__articles">
                  <ul class="support-article-list">
                    <li
                      v-for="article in category.articles" 
                      :key="article.id"
                      class="support-article-list__item-wrapper"
                    >
                      <router-link
                        :to="`/support/${$route.params.collectionId}/${article.slug || article.id}`"
                        class="support-article-list__item"
                      >
                        <div class="support-article-list__content">
                          <div class="support-article-list__details">
                            <p class="support-article-list__title">
                              {{ article.name || article.title }}
                            </p>
                            <p v-if="article.preview" class="support-article-list__preview">
                              {{ article.preview }}
                            </p>
                          </div>
                        </div>
                      </router-link>
                    </li>
                  </ul>
                </div>
                
                <!-- No articles message -->
                <div v-else class="support-category__empty">
                  No articles in this category yet.
                </div>
              </div>
            </div>

            <!-- No categories message -->
            <div v-else class="support-empty">
              <p>No categories found in this collection.</p>
            </div>
          </div>

          <!-- Collection not found -->
          <div v-if="!loading && !error && !collection" class="support-empty">
            <h1>Collection Not Found</h1>
            <p>The requested collection could not be found.</p>
            <router-link to="/support">
              ← Back to all collections
            </router-link>
          </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MetaInfo from "@/components/MetaInfo";
import Navigation from "@/components/navigation/Main";
import SupportSidebar from "@/components/Support/SupportSidebar";
import SkeletonLoader from "@/components/Support/SkeletonLoader";
import helpScoutService from "@/services/helpscout";
import helpScoutState from "@/services/helpscoutState";

export default {
  name: "SupportCollectionPage",
  components: {
    MetaInfo,
    Navigation,
    SupportSidebar,
    SkeletonLoader,
  },
  data() {
    return {
      collection: null,
      categories: [],
      loading: true,
      error: null,
      debugData: null,
      meta: {
        title: "",
        description: "",
      },
    };
  },
  async created() {
    await this.loadCollection();
  },
  watch: {
    '$route.params.collectionId': {
      handler() {
        this.loadCollection();
      },
    },
  },
  methods: {
    async loadCollection() {
      try {
        this.loading = true;
        this.error = null;
        this.collection = null;
        this.categories = [];
        this.meta.title = "";
        this.meta.description = "";

        const collectionIdentifier = this.$route.params.collectionId;

        // Use shared state to get collections (ensures shared state is populated)
        await helpScoutState.getCollections(helpScoutService);
        
        // Find collection by ID or slug using shared state helper
        const foundCollection = helpScoutState.findCollection(collectionIdentifier);

        if (foundCollection) {
          // Get full collection details
          const collectionDetail = await helpScoutService.getCollection(foundCollection.id);
          
          if (collectionDetail) {
            this.collection = collectionDetail;
            
            this.meta.title = `${this.collection.name} - Support Documentation`;
            this.meta.description =
              this.collection.description ||
              `Browse ${this.collection.name} support documentation and help articles.`;
          }

          // Use shared state to get categories (with caching)
          this.categories = await helpScoutState.getCategories(foundCollection.id, helpScoutService);

          // Fetch articles for all categories in parallel (optimized - single API call per category)
          // Use Promise.allSettled to prevent single failures from breaking everything
          const categoryPromises = this.categories.map(async (category) => {
            try {
              // Directly fetch articles by category (skip getCategory call - we already have category metadata)
              const articlesData = await helpScoutService.getArticlesByCategory(category.id);
              if (articlesData) {
                // Filter to only show published articles
                category.articles = articlesData
              } else {
                category.articles = [];
              }
            } catch (err) {
              console.warn(`Could not load articles for category ${category.id}:`, err);
              category.articles = [];
            }
          });

          // Wait for all category article fetches to complete (with graceful failure handling)
          await Promise.allSettled(categoryPromises);

          // Hide empty categories (e.g. Help Scout's auto-created "Uncategorized")
          this.categories = this.categories.filter(
            (category) => category.articles && category.articles.length > 0
          );

          this.debugData = {
            collection: this.collection,
            categories: this.categories,
          };
        } else {
          // Collection not found
          this.collection = null;
        }

        if (!this.meta.title) {
          this.meta.title = "Support Documentation";
        }
        if (!this.meta.description) {
          this.meta.description =
            "Browse our support documentation and help articles.";
        }

      } catch (error) {
        console.error('Error loading collection:', error);
        
        // Check if it's an authentication error
        const isAuthError = error.response && (error.response.status === 401 || error.response.status === 403);
        
        this.error = {
          message: error.message || 'Failed to load collection',
          isAuthError,
          details: error.response ? {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data
          } : null
        };

        this.debugData = { error: this.error };
        if (!this.meta.title) {
          this.meta.title = "Support Documentation";
        }
        if (!this.meta.description) {
          this.meta.description =
            "Browse our support documentation and help articles.";
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

